const PLATFORM_BY_MEDIA_SOURCE = {
  ads: 'Google',
  ads25: 'Google',
  ads30: 'Google',
  googleadwords_int: 'Google',
  google_ads: 'Google',
  google: 'Google',
  meta: 'Meta',
  metavo: 'Meta',
  facebook_ads: 'Meta',
  facebookads_int: 'Meta',
  meta_ads: 'Meta',
}

const ACTION_LABELS = {
  BUDGET_CHANGE: '预算调整',
  BID_CHANGE: '出价调整',
  BID_STRATEGY_CHANGE: '出价策略调整',
  TARGET_ROAS_CHANGE: '目标 ROAS 调整',
  TARGET_CPA_CHANGE: '目标 CPA 调整',
  TARGETING_CHANGE: '定向调整',
  STATUS_CHANGE: '状态调整',
  SCHEDULE_CHANGE: '排期调整',
  BUDGET_SCHEDULE_CHANGE: '预算排期调整',
  CREATIVE_CHANGE: '素材调整',
  OPTIMIZATION_CHANGE: '优化目标调整',
  CAMPAIGN_DIMENSION_CHANGE: 'Campaign 信息调整',
  OTHER: '其他调整',
  UNCLASSIFIED: '未分类调整',
}

const FIELD_LABELS = {
  amount_micros: '预算',
  budget: '预算',
  daily_budget: '每日预算',
  lifetime_budget: '总预算',
  'target_roas.target_roas': '目标 ROAS',
  'target_cpa.target_cpa_micros': '目标 CPA',
  status: '状态',
  budget_scheduling_state: '预算排期',
  'app_ad.images': '图片素材',
  'app_ad.youtube_videos': '视频素材',
  'app_ad.headlines': '标题素材',
  'app_ad.descriptions': '描述素材',
}

const STATUS_LABELS = {
  0: '未指定',
  1: '未知',
  2: '启用',
  3: '暂停',
  4: '移除',
  active: '启用',
  enabled: '启用',
  paused: '暂停',
  disabled: '停用',
  archived: '归档',
  removed: '移除',
}

const SOURCE_LABELS = {
  Web: '网页',
  API: 'API',
  Rule: '自动规则',
  Editor: '编辑器',
  App: '应用',
  Tool: '工具',
  System: '系统',
  Unknown: '未知来源',
}

const EVIDENCE_LEVEL_LABELS = {
  platform_before_after: '平台前后值',
  platform_action_only: '平台操作记录',
  inferred: '关联推断',
}

const formatNumber = (value) =>
  new Intl.NumberFormat('zh-CN', { maximumFractionDigits: 2 }).format(value)

export const getPlatformChangePlatform = (mediaSource) => {
  const normalizedMediaSource = String(mediaSource || '').trim().toLowerCase()
  return PLATFORM_BY_MEDIA_SOURCE[normalizedMediaSource] || 'N/A'
}

export const getPlatformChangePlatformCode = (mediaSource) => {
  const platform = getPlatformChangePlatform(mediaSource)
  if (platform === 'Google') return 'google_ads'
  if (platform === 'Meta') return 'meta_ads'
  return null
}

export const isPlatformChangeSupported = (mediaSource) =>
  getPlatformChangePlatform(mediaSource) !== 'N/A'

export const isEventOnlyCampaignDayRow = (row) =>
  row?.rowType === 'event_only' || row?.hasManualNote === false

export const getEventOnlyCountryDisplay = (row) => {
  const countryCount = Number(row?.affectedCountryCount)
  if (isEventOnlyCampaignDayRow(row) && countryCount > 1) {
    const countries = Array.isArray(row?.affectedCountries)
      ? row.affectedCountries.filter(Boolean).join(', ')
      : ''
    return {
      label: `${countryCount} 个国家`,
      title: countries || undefined,
    }
  }

  return { label: row?.country || '', title: undefined }
}

export const isEventOnlyMultiScope = (row) =>
  isEventOnlyCampaignDayRow(row) && (
    row?.isCombinedCampaign === true || Number(row?.affectedCountryCount) > 1
  )

export const getNoteReasonLabel = (row) =>
  row?.reason?.trim?.() || '未填写 Note'

export const getPlatformChangesRequest = (row) => {
  if (!isPlatformChangeSupported(row?.mediaSource)) return null

  if (!isEventOnlyCampaignDayRow(row)) {
    const noteId = row?.noteId ?? row?.id
    return noteId ? [`/troas/notes/${noteId}/platform-changes`] : null
  }

  const platform = getPlatformChangePlatformCode(row?.mediaSource)
  if (!row?.campaignId || !row?.date || !platform) return null
  return [
    '/troas/notes/platform-changes',
    { campaignId: row.campaignId, date: row.date, platform },
  ]
}

export const getCoverageState = (coverage) => {
  const status = String(coverage?.status || coverage || '').toLowerCase()

  switch (status) {
    case 'collected':
    case 'available':
      return { status: 'available', label: '已完整采集', className: 'text-emerald-600' }
    case 'partial':
      return { status: 'partial', label: '部分采集', className: 'text-amber-600' }
    case 'event_matched':
    case 'event-matched':
      return { status: 'event_matched', label: '已匹配事件', className: 'text-emerald-600' }
    case 'not_collected':
    case 'not-collected':
      return { status: 'not_collected', label: '未采集', className: 'text-amber-600' }
    case 'unsupported':
      return { status: 'unsupported', label: '暂不支持', className: 'text-muted-foreground' }
    case 'unavailable':
      return { status: 'unavailable', label: '服务不可用', className: 'text-destructive' }
    default:
      return { status: 'unknown', label: '状态未知', className: 'text-muted-foreground' }
  }
}

export const getPlatformChangesPayload = (response) => {
  const payload = response?.data || response || {}
  const events = payload.items || payload.events || payload.platformChanges || payload.changes || []

  return {
    coverage: payload.coverage || { status: payload.coverageStatus || payload.status },
    events: Array.isArray(events) ? events : [],
    total: Number(payload.total ?? events.length ?? 0),
    campaignName: payload.campaignName,
    date: payload.date,
    timezone: payload.timezone,
  }
}

export const formatEvidence = (evidence) => {
  if (evidence === null || evidence === undefined || evidence === '') return '无'
  return typeof evidence === 'string' ? evidence : JSON.stringify(evidence, null, 2)
}

export const getActionLabel = (actionType) =>
  ACTION_LABELS[String(actionType || '').toUpperCase()] || actionType || '未知调整'

export const getFieldLabel = (fieldPath) =>
  FIELD_LABELS[String(fieldPath || '').toLowerCase()] || fieldPath || '调整内容'

export const getSourceLabel = (sourceType) =>
  SOURCE_LABELS[sourceType] || sourceType || '未知来源'

export const getEvidenceLevelLabel = (evidenceLevel) =>
  EVIDENCE_LEVEL_LABELS[evidenceLevel] || evidenceLevel || '未知证据'

export const formatBusinessDate = (date) => {
  const value = String(date || '')
  if (!/^\d{8}$/.test(value)) return value || '未知日期'
  return `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6, 8)}`
}

export const formatActionTime = (value) => {
  if (!value) return '未知时间'
  return String(value).replace('T', ' ').replace(/([+-]\d{2}:\d{2}|Z)$/, '')
}

export const formatEvidenceValue = (evidence, side) => {
  const normalizedKey = `${side}ValueNormalized`
  const valueKey = `${side}Value`
  const normalizedValue = evidence?.[normalizedKey]
  const rawValue = evidence?.[valueKey]
  const value = normalizedValue ?? rawValue
  if (value === null || value === undefined || value === '') return '无'

  const fieldPath = String(evidence?.fieldPath || '').toLowerCase()
  if (fieldPath === 'status') {
    const statusKey = String(value).toLowerCase()
    return STATUS_LABELS[statusKey] || String(value)
  }

  const numericValue = Number(value)
  const isNumeric = Number.isFinite(numericValue)
  if (
    isNumeric &&
    (evidence?.valueUnit === 'ratio' || fieldPath.includes('target_roas'))
  ) {
    return `${formatNumber(numericValue * 100)}%`
  }

  if (isNumeric && evidence?.valueUnit === 'micros') {
    const amount = normalizedValue == null ? numericValue / 1_000_000 : numericValue
    const currency = evidence?.currencyCode ? ` ${evidence.currencyCode}` : ''
    return `${formatNumber(amount)}${currency}`
  }

  if (Array.isArray(value)) return `${value.length} 项`
  if (typeof value === 'boolean') return value ? '是' : '否'
  if (typeof value === 'object') return JSON.stringify(value)
  if (isNumeric) return formatNumber(numericValue)
  return String(value)
}
